import { useLayoutEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import axios from 'axios';
import { useAuthStore } from '../store/auth.ts';

export function Login(): JSX.Element {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Login is always shown in light theme regardless of the user's dark-mode
  // preference; their real theme is restored when navigating away.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.remove('dark');
    return () => {
      try {
        const stored = localStorage.getItem('wapsign.theme');
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (stored === 'dark' || (!stored && prefersDark)) html.classList.add('dark');
      } catch {
        /* ignore */
      }
    };
  }, []);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await login(email, password);
      await navigate({ to: '/dashboard' });
    } catch (e) {
      // Only a 401 means bad credentials. Mapping every failure (429 rate
      // limit, 5xx, network) to "Invalid email or password" hid the real
      // cause from users with correct credentials.
      if (axios.isAxiosError(e) && e.response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else if (axios.isAxiosError(e) && e.response?.status === 401) {
        setError('Invalid email or password.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel — desktop only */}
      <aside className="hidden lg:flex relative flex-col justify-between overflow-hidden bg-[hsl(var(--accent-ink))] text-background p-12">
        {/* Decorative cream wash + grain — matches body texture but inverted */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-25 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 50rem 35rem at 15% 110%, hsl(var(--accent-cream)), transparent 60%)',
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />

        <div className="relative flex items-center gap-3">
          <img
            src="/icons/wapsign-mark.svg"
            alt=""
            className="size-9 rounded-[25%] object-contain ring-1 ring-white/15"
          />
          <span className="font-sans text-3xl leading-none">WapSign</span>
        </div>

        <div className="relative max-w-md space-y-6">
          <p className="status-label !text-white/55">Multi-tenant PDF signing</p>
          <h1 className="font-sans text-5xl xl:text-6xl leading-[1.05] !text-background">
            Sign documents <span>that travel.</span>
          </h1>
          <p className="text-base text-white/70 leading-relaxed">
            Upload a PDF, place signature boxes, send it. Recipients sign in the
            browser — no account required for them.
          </p>
        </div>

        <div className="relative flex items-end justify-between gap-6">
          <p className="mono-meta !text-white/40 max-w-xs leading-relaxed">
            Built by Wapplify · {new Date().getFullYear()}
          </p>
          <svg
            aria-hidden
            viewBox="0 0 80 80"
            className="size-16 text-white/15 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <circle cx="40" cy="40" r="35" />
            <circle cx="40" cy="40" r="28" />
            <path d="M22 42 l10 10 l26 -26" strokeWidth="2.5" />
          </svg>
        </div>
      </aside>

      {/* Form panel */}
      <section className="flex items-center justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-sm space-y-8">
          {/* Compact brand on mobile */}
          <div className="lg:hidden flex items-center justify-center gap-2.5">
            <img
              src="/icons/wapsign-mark.svg"
              alt=""
              className="size-8 rounded-[25%] object-contain ring-1 ring-border"
            />
            <span className="font-sans text-2xl leading-none text-ink">WapSign</span>
          </div>

          <header className="space-y-2 text-center lg:text-left">
            <p className="status-label">Welcome back</p>
            <h2 className="font-sans text-4xl leading-none text-ink">
              Sign in
            </h2>
            <p className="text-sm text-muted-foreground">
              Continue to your workspace.
            </p>
          </header>

          <form className="space-y-5" onSubmit={submit}>
            <div className="space-y-1.5">
              <label htmlFor="email" className="field-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="field-label">
                  Password
                </label>
                <a
                  href="https://office.wapplify.app/forgot-password"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
                >
                  Forgot password?
                </a>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 inline-flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  {showPw ? <Icon name="visibility_off" size={18} /> : <Icon name="visibility" size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              className="btn-primary w-full py-2.5"
            >
              {pending ? 'Signing in…' : (<>Sign in <Icon name="arrow_forward" size={18} /></>)}
            </button>
          </form>

          <footer className="pt-2 border-t border-border/60">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Need access? Ask your workspace admin to send you an invite link.
            </p>
          </footer>
        </div>
      </section>
    </div>
  );
}
